import type {
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
  GetApiV1ThreeLetterCodePartnerAdminsUsers200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { useConfiguration } from '~/contexts/ConfigurationProvider';
import { convertDateTime } from '~/utils/dateTime';
import { isPartnerFeatureFlagEnabled } from '~/utils/isPartnerFeatureFlagEnabled';
import type { useUserList } from '../../hooks';
import type { UserListPresentation } from '../../presentations';

type UseUserListProps = ReturnType<typeof useUserList>['state'];
type PresentationProps = React.ComponentProps<typeof UserListPresentation>;

type Args = {
  /** API から取得した利用者一覧データ */
  usersData: GetApiV1ThreeLetterCodePartnerAdminsUsers200;
  /**
   * 検索条件が適用されているか
   *
   * true のとき 0 件でも Empty 画面を表示しない（PEN-309 対応パターン）
   */
  isFiltered: UseUserListProps['isFiltered'];
  /** 環境フィーチャーフラグ（staging / development 環境で true） */
  enableStaging: UseUserListProps['enableStaging'];
  /** PartnerFeatureFlag 一覧を含む API レスポンス（一覧の取り出しはこの関数内で行う） */
  partnerFeatureFlagsData: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
  /** アプリケーション設定 */
  applicationConfiguration: ReturnType<typeof useConfiguration>['applicationConfiguration'];
};

type IConvertToPresentationProps = (params: Args) => {
  rows: PresentationProps['rows'];
  /** 利用者が 0 件かどうか */
  isEmpty: boolean;
  /** アクティベーションコード関連 UI を表示するか */
  isActivationCodeEnabled: PresentationProps['isActivationCodeEnabled'];
  /** 「電話番号」列を表示するか */
  showPhoneNumberColumn: PresentationProps['showPhoneNumberColumn'];
};

/**
 * API 型（snake_case）を UserList Presentation の props 型（camelCase）に変換する。
 *
 * - 出力型は `UserListPresentation` の Props から導出し、型乖離を防ぐ
 * - isEmpty フラグを導出して Container 層での表示出し分けに使用する
 * - 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
 * - ページネーション（pager）の組み立ては Container 層（generatePagerProps）が担う
 * - 未設定値（email / phone_number / billable_from の null）は表示用の '-' に正規化する。
 *   Presentation 側でフォールバックしないことで「'-' を出すのは converter」に責務を一本化する
 *
 * アクティベーションコード UI の表示可否（isActivationCodeEnabled）は PartnerFeatureFlag
 * （activation_code）から導出してここで組み立てる（MMT-156）。
 * development / staging 環境では enableStaging を OR 条件に加え、activation_code フラグ未設定でも
 * 開発者・QA が UI を確認できるようにする。
 *
 * 「電話番号」列の表示可否（showPhoneNumberColumn）はこの関数で導出し、Presentation には
 * 真偽値だけを渡す（Presentation 層に login_method の条件ロジックを持ち込まない）。
 * login_method が 'phone_number' のときのみ電話番号ログインとみなし、'email' / 未設定
 * （applicationConfiguration 自体が未取得の null / undefined を含む）はすべて false に落とす。
 * アクティベーションコードの有効・無効には連動しない（MMT-290）。
 *
 * - 電話番号ログイン: 「メールアドレス」列に加えて「電話番号」列を表示する
 * - メールログイン: 「メールアドレス」列のみを表示する
 *
 * 「メールアドレス」列は login_method に関わらず常時表示するため、フラグを導出しない（MMT-296）。
 * 電話番号ログインのテナントでも家族利用者はメールアドレスでログインするため、テナント単位で
 * メールアドレスを隠すと同一テナント内の該当利用者の情報が管理画面から見えなくなる。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  usersData,
  isFiltered,
  enableStaging,
  partnerFeatureFlagsData,
  applicationConfiguration,
}) => {
  const users = usersData.users;
  const partnerFeatureFlags = partnerFeatureFlagsData.partner_feature_flags;

  const rows: PresentationProps['rows'] = users.map(user => ({
    id: user.id,
    clientName: user.client.name,
    name: user.full_name,
    isActivated: user.is_activated,
    activationCode: user.activation_code ?? undefined,
    // API は未設定を null で返すが空文字が返るケースも想定されるため、`??` ではなく `||` で '-' に寄せる
    email: user.email || '-',
    phoneNumber: user.phone_number || '-',
    billableFrom: user.billable_from
      ? formatDate({ date: new Date(`${user.billable_from}T00:00:00`), formatType: 'yyyy/M/d' })
      : '-',
    createdAt: convertDateTime(user.created_at),
    updatedAt: formatDate({ date: new Date(user.updated_at * 1000), formatType: 'yyyy/M/d H:mm' }),
  }));

  // 検索条件あり × 0 件は Empty 画面を表示しない（PEN-309 対応パターン）
  const isEmpty = users.length === 0 && !isFiltered;

  // development / staging 環境では enableStaging を OR 条件として加えることで、
  // activation_code フラグが未設定でも開発者・QA がアクティベーションコード UI を確認できる。
  const isActivationCodeEnabled =
    isPartnerFeatureFlagEnabled(partnerFeatureFlags, 'activation_code') || enableStaging;

  const isLoginMethodPhoneNumber =
    applicationConfiguration?.application_configuration?.login_method === 'phone_number';

  const showPhoneNumberColumn = isLoginMethodPhoneNumber;

  return { rows, isEmpty, isActivationCodeEnabled, showPhoneNumberColumn };
};
